import { useEffect, useState } from 'react';

import { useRelayEnvironment } from 'react-relay';

import type { FomoCollaborator } from '../types';

import { fetchUsersByAaids } from './fetchUsersByAaids';

const MAX_AAIDS = 3;

export interface UseFomoCollaboratorsBatchedResult {
	collaborators: FomoCollaborator[];
	/** Whether the underlying batched fetch rejected (network/abort/5xx). Not set when AGG returned partial results. */
	error: unknown | null;
	isReady: boolean;
}

/**
 * Resolve a list of Atlassian Account IDs (AAIDs) to `FomoCollaborator[]`
 * with a single batched AGG `users(accountIds:)` request.
 *
 * - Caps input at 3 AAIDs (the avatar group's max display count).
 * - Returns immediately ready when disabled or no AAIDs.
 * - Surfaces `error` so callers can attach `errorMessage` to operational analytics.
 */
export function useFomoCollaboratorsBatched(
	aaids: ReadonlyArray<string>,
	{ enabled = true }: { enabled?: boolean } = {},
): UseFomoCollaboratorsBatchedResult {
	const environment = useRelayEnvironment();
	const capped = aaids.slice(0, MAX_AAIDS);
	const isNoop = !enabled || capped.length === 0;

	const [state, setState] = useState<UseFomoCollaboratorsBatchedResult>({
		collaborators: [],
		isReady: isNoop,
		error: null,
	});

	const key = isNoop ? '__noop__' : JSON.stringify(capped);

	useEffect(() => {
		if (isNoop) {
			setState({ collaborators: [], isReady: true, error: null });
			return;
		}

		let cancelled = false;
		setState({ collaborators: [], isReady: false, error: null });

		fetchUsersByAaids(environment, capped).then(({ collaborators, error }) => {
			if (cancelled) return;
			setState({ collaborators, isReady: true, error });
		});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, isNoop, environment]);

	return state;
}
